import { TabStyles, TextFieldWidget } from '@tableau/widgets';
import { FormikContextType } from 'formik';
import { componentRegistryEntry } from '../../../../directives/tbSiteSettingsForServerAdminFormAdapter';
import { Field, FieldProps, useFormikContext } from '../../../../FormikWrapperForStubbing';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as _ from 'underscore';
import { appStore } from '../../../../../app/AppStore';
import { SessionUserSelectors } from '../../../../../app/selectors/SessionInfoSelectors';
import { AppState } from '../../../../../app/state/AppState';
import { Messages } from '../../../../../codegen/Localize';
import { SiteSwitchService } from '../../../../../ts/services/SiteSwitchService';
import { SiteIdValidRegExp } from '../../../../controllers/SiteSettingsForServerAdminFormCtrl';
import { ellipsisStyle, formError, wideWidth } from '../../../../styles/CommonStyles';
import { generatedUrlDivStyle, tableLayout } from '../../../styles/SiteNameAndIdStyles';
import { useSelector } from '../../../utils';
import { SiteSettingsForServerAdminProps } from '../../ReactSiteSettingsForServerAdmin';
import { SettingsSection } from '../../SettingsSection';
import { calculateUrlFromUrlName, calculateUrlNameFromSiteName, SiteIdValidator, useShouldCopySiteNameToUrl } from './SiteNameAndIdBusinessLogic';
import { SiteNameRow, useSiteNameChangeHandler } from './SiteNameRow';
import { SiteUrlRow, useUrlNameChangeHandler } from './SiteUrlRow';

// individual sections can specify the settings they depend on to clean up their props
export type SiteNameAndIdSectionProps = SiteSettingsForServerAdminProps<'name' | 'urlName'>;

export const SiteNameAndIdSection: React.FunctionComponent<SiteNameAndIdSectionProps> = props => {
  const formik = useFormikContext<SiteNameAndIdSectionProps>();

  const url = useMemo(
    calculateUrlFromUrlName(formik.values.settings.urlName),
    [formik.values.settings.urlName],
  );

  const shouldCopySiteNameToUrl = useShouldCopySiteNameToUrl(props);
  const [isUrlNameModified, setUrlNameModified] = useState(false);

  const handleSiteNameChange = useSiteNameChangeHandler(formik, shouldCopySiteNameToUrl, isUrlNameModified);
  const handleUrlNameChange = useUrlNameChangeHandler(formik, setUrlNameModified);
  const urlNameValidator = useUrlNameValidator(props.isDefaultSite);

  const isMultisite = !formik.values.hideNameAndId ?? useSelector(SessionUserSelectors.isSessionMultiSite);

  return isMultisite && <SettingsSection title={Messages.settings_site_name()} testId={'name-and-id-fields'}>
    <table className={tableLayout}>
      <tbody>
      <SiteNameRow name={formik.values.settings.name}
                   onChange={handleSiteNameChange}/>
      {
        !props.isDefaultSite &&
        <SiteUrlRow urlName={formik.values.settings.urlName}
                    onChange={handleUrlNameChange}
                    validator={urlNameValidator}/>
      }
      <tr>
        <td/>
        <td>
          <div className={generatedUrlDivStyle}>
            <span>{Messages.url_colon()}</span>
            <span title={url} data-tb-test-id={'origin-url'}>&nbsp; {url}</span>
          </div>

          {
            props.isDefaultSite &&
            <div>
              <span data-tb-test-id={'default-site-url-message'}>
                {Messages.defaultSiteURLDetails()}
              </span>
            </div>
          }

          {
            !props.isDefaultSite &&
            formik.touched?.settings?.urlName &&
            formik.errors?.settings?.urlName &&

            <div className={formError}>
              <span data-tb-test-id={'site-id-error'}>{Messages.siteIDInvalid()}</span>
            </div>
          }
        </td>
      </tr>
      </tbody>
    </table>
  </SettingsSection>;
};

export const SiteNameAndIdSpecs = componentRegistryEntry({
  name: 'SiteNameAndIdSection',
  component: SiteNameAndIdSection,
  meta: {
    urlName: {
      initError: props => SiteIdValidator(props.settings?.urlName),
      initTouched: props => !!SiteIdValidator(props.settings?.urlName)
    }
  }
});

function useUrlNameValidator(isDefaultSite: boolean) {
  return useCallback((value: string) => isDefaultSite ? undefined : SiteIdValidator(value), [isDefaultSite]);
}
